import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { Stack } from "@mui/material";
import { gridActionSx } from "../widgets/gridActionSx";
import { GET_REGRADING_ALERTS } from "../../backend/graphql";
import {
  GraphQLDataGrid,
  GraphQLGridParameters,
} from "../widgets/GraphQLDataGrid";
import { adminTagsColumn, handleNavigateGrading, renderPricing } from "./Stock";
import { Change } from "./GradingPrice";
import { useAppSelector, useHistoryState } from "../../app/hooks";
import { Roles } from "../../app/features/auth/authTypes";
import QuickFilter from "../widgets/QuickFilter";
import PageTitle from "../widgets/PageTitle";

const renderChange = (params: any) =>
  params.value != null ? <Change share={params.value} /> : null;

const gridParameters = (
  onlyWins: boolean,
  greatChances: boolean,
  role: Roles,
): GraphQLGridParameters => {
  // Only the stones still available.
  let filter =
    "isGIACertified == true && noLongerAvailable == false && regradingBetterProbability != null";
  if (onlyWins) filter += " && regradingLowestChange > 0";
  if (greatChances) filter += " && regradingBetterProbability > 0.7";
  return {
    graphqlQuery: GET_REGRADING_ALERTS,
    graphqlFilter: filter,
    graphqlItemsPath: (data) => data.diamonds,
    rowHeight: 64,
    allRows: true,
    fields: [
      { field: "representationImageID", label: "Image", type: "image" },
      { field: "id", label: "ID", visible: false, width: 300, type: "uuid" },
      { field: "source", label: "Source", flex: 0.5, visible: false },
      { field: "sKU", label: "SKU", width: 80 },
      { field: "carat", label: "Carat", width: 80, type: "number" },
      { field: "cut", label: "Cut", visible: false },
      { field: "shape", label: "Shape", visible: false },
      { field: "clarity", label: "Clarity", width: 80 },
      { field: "color", label: "Color", flex: 1 },
      {
        field: "langermanColor",
        label: "Langerman Color",
        flex: 1,
        visible: false,
      },
      { field: "flat", label: "Flat", type: "number", visible: false },
      { field: "melee", label: "Melee", type: "number", visible: false },
      {
        field: "isGIACertified",
        label: "GIA?",
        flex: 0.5,
        type: "boolean",
        visible: false,
      },
      { field: "tags", label: "Tags", flex: 1 },
      ...adminTagsColumn(role),
      { field: "price", label: "Price", flex: 0.5, type: "number" },
      {
        field: "currentPricePercentile",
        label: "Pricing",
        flex: 0.5,
        type: "number",
        renderCell: (params: any) => renderPricing(params.value),
      },
      {
        field: "predictionRank",
        label: "Prediction rank",
        type: "number",
      },
      {
        field: "regradingBetterProbability",
        // The chance of a better price.
        label: "Probability",
        type: "number",
        defaultSort: "desc",
        renderCell: (params: any) =>
          params.value != null ? Math.round(params.value * 100) + "%" : null,
      },
      {
        field: "regradingLowestChange",
        label: "Min",
        type: "number",
        renderCell: renderChange,
      },
      {
        field: "regradingHighestChange",
        label: "Max",
        type: "number",
        renderCell: renderChange,
      },
      { field: "pricingRemark", label: "Remark", flex: 2, visible: false },
      {
        field: "lastFantasyUpdate",
        label: "Last Check",
        type: "dateTimeShort",
        visible: false,
      },
      {
        field: "ignorePricing",
        label: "Ignore Price",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "noLongerAvailable",
        label: "Unavailable",
        type: "boolean",
        width: 100,
        visible: false,
      },
    ],
    maxActions: 1,
    getActions: ({ row }) => [
      <GridActionsCellItem
        icon={<LeaderboardIcon />}
        label="Grading"
        sx={gridActionSx}
        onClick={() => handleNavigateGrading(row)}
      />,
    ],
  };
};

// The GIA certified stones whose regrading could pay, from what the worker stores on each stone's
// analysis: the chance that another likely color is priced above its own, and the lowest and
// highest price of the likely colors other than its own, as a change from its own.
const RegradingAlert = () => {
  const [onlyWins, setOnlyWins] = useHistoryState("onlyWins", false);
  const [greatChances, setGreatChances] = useHistoryState(
    "greatChances",
    false,
  );
  const role = useAppSelector((state) => state.auth.role);
  const parameters = gridParameters(onlyWins, greatChances, role);
  return (
    <Stack spacing={1} useFlexGap>
      <PageTitle />
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <QuickFilter
          active={onlyWins}
          label="Only wins"
          toggle={(active) => setOnlyWins(!active)}
        />
        <QuickFilter
          active={greatChances}
          label="Great chances"
          toggle={(active) => setGreatChances(!active)}
        />
      </Stack>
      <GraphQLDataGrid parameters={parameters} elevated />
    </Stack>
  );
};

export default RegradingAlert;
